import React, { useContext, useMemo, useEffect, useCallback, useRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { BaseRatePackageView } from 'selectors/offerPage/offerPageSelectors'
import { arePricesLoading, getBaseRatePackagesView, getFromDurationLabel, getCheapestSelectedPackageOptionWithDefault, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'LuxKit/TextLink'
import WalledContent from 'components/Common/WalledContent'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import GeoContext from 'contexts/geoContext'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import { isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import Group from 'components/utils/Group'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { unique } from 'lib/array/arrayUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { addFlightPrice, feesToObject, priceWithoutPropertyFees } from 'lib/offer/pricing'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(4)};
`

const FlightPrice = styled(PriceDetails)`
  margin-top: ${rem(8)};
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(20)};
  text-align: center;
`

const LoyaltyPointsWrapper = styled.div`
  margin: ${rem(8)};
`

interface Props {
  offer: App.Offer
  className?: string
  cheapestSelectedOption: App.OfferPackageOptionView
  baseRatePackagesView: Array<BaseRatePackageView>
  onViewPackageOptions: () => void
  pricesLoading?: boolean
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  rooms: Array<App.Occupants> | undefined
  checkIn?: string
  duration?: number
  durationLabel: string
  canViewLuxPlusBenefits: boolean
  availableForPurchase?: boolean
}

function AlwaysOnSummary(props: Props) {
  const {
    offer,
    className,
    cheapestSelectedOption: packageView,
    onViewPackageOptions,
    pricesLoading,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    duration,
    baseRatePackagesView,
    durationLabel,
    canViewLuxPlusBenefits,
    availableForPurchase,
  } = props

  const intl = useIntl()
  const { currentCurrency } = useContext(GeoContext)
  const offerDispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerDispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }, [offerDispatch])

  const {
    flightPrice,
    package: pkg,
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
  } = packageView

  const saleUnit = useMemo(() => {
    if (offer.bundledWithFlightsOnly) return ' package*'
    return offer.saleUnit
  }, [offer.saleUnit, offer.bundledWithFlightsOnly])

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const hideNoCacheMessaging = !hasFlights && offer.bundledWithFlightsOnly
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS, [checkInMoment])

  const [pricingToDisplay, memberPricingToDisplay] = useMemo(() => {
    let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
    let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
    if (offer.bundledWithFlightsOnly) {
      if (flightsEnabled && !hideNoCacheMessaging && hasFlights) {
        pricingToDisplay = addFlightPrice(pricingToDisplay, flightPrice)
        memberPricingToDisplay = memberPricingToDisplay && addFlightPrice(memberPricingToDisplay, flightPrice)
      } else {
        pricingToDisplay = undefined
        memberPricingToDisplay = undefined
      }
    }
    return [pricingToDisplay, memberPricingToDisplay]
  }, [hotelPricing, hotelMemberPricing, flightPrice, offer.bundledWithFlightsOnly, flightsEnabled, hasFlights, hideNoCacheMessaging])

  const fees = pricingToDisplay ? feesToObject(pricingToDisplay.fees) : {}

  const showMemberPrice = !!memberPricingToDisplay && memberPricingToDisplay.price > 0

  const hotelValueOrHotelMemberValue = (canViewLuxPlusBenefits ? hotelMemberPricing?.value : hotelPricing.value) ?? 0
  const hotelDiscountOrHotelMemberDiscount = (canViewLuxPlusBenefits ? hotelMemberPricing?.discountPercent : hotelPricing.discountPercent) ?? 0
  const shouldShowValue = pricingToDisplay && hotelValueOrHotelMemberValue > pricingToDisplay.price && !pkg.roomRate?.inclusionsHideValue

  const baseRatePackageView = useMemo(() => {
    return baseRatePackagesView.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.id === pkg.id,
    )
  }, [baseRatePackagesView, pkg.duration, pkg.id])

  const baseRateValue = canViewLuxPlusBenefits ? baseRatePackageView?.memberValue : baseRatePackageView?.value
  const inclusionsAmount = (canViewLuxPlusBenefits ? pkg.totalInclusionsAmount : pkg.inclusionsAmount) ?? 0
  const pkgValueOrPkgMemberValue = canViewLuxPlusBenefits ? pkg.memberValue : pkg.value

  // We use base rate cost to calculate new totalValue and discountPercent, using the isBaseRate flag on rate plan level.
  // This shows a bigger discount
  const valuedUpTo = useMemo(() => {
    if (baseRateValue && (baseRateValue + inclusionsAmount) > pkgValueOrPkgMemberValue) {
      const displayedHotelValueBase = baseRateValue + inclusionsAmount + (fees.property ?? 0)
      const displayedHotelValue = displayedHotelValueBase
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, hotelPricing.price)
      return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
    }

    return { value: hotelValueOrHotelMemberValue, percent: hotelDiscountOrHotelMemberDiscount }
  }, [baseRateValue, inclusionsAmount, pkgValueOrPkgMemberValue, hotelValueOrHotelMemberValue, hotelDiscountOrHotelMemberDiscount, fees.property, hotelPricing.price])

  const shouldShowDiscountPercent = valuedUpTo.percent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showDiscountPill = !offer?.isDiscountPillHidden && shouldShowDiscountPercent && !pricesLoading && !offer?.bundledWithFlightsOnly

  const showPriceDetails = !!fees.taxesAndFees || !!fees.property || config.agentHub.isEnabled
  const checkOut = checkInMoment ? checkInMoment.clone().add(pkg.duration, 'days').format(ISO_DATE_FORMAT) : undefined
  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialOfferPageRooms,
  })

  const viewOptionsLabel = intl.formatMessage(
    {
      id: 'x+kVZ79o7U',
      defaultMessage: 'View {travelSynonym} options',
      description: 'Button text to view travel options on always-on hotel offer page',
    },
    { travelSynonym: config.brandConfig.travelSynonym },
  )

  const viewOptionsShortLabel = intl.formatMessage({
    id: 'BthsTrf6F+',
    defaultMessage: 'View options',
    description: 'Button text to view package options on always-on hotel offer page',
  })

  const pkgAvailableRate = useMemo(() => offerAvailableRates?.rates?.find(rate => rate.packageUniqueKey === pkg.uniqueKey), [pkg, offerAvailableRates])
  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelPricing.price, pkg, pkgAvailableRate)
  const showHotelPrice = !offer.bundledWithFlightsOnly || (flightsEnabled && !hideNoCacheMessaging && hasFlights)

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName
  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: pkg.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !offer.bundledWithFlightsOnly || !hasFlights || !flightsEnabled,
    }),
  ], [offer, packageView, hasFlights, flightsEnabled, currentRegionCode, pkg.duration])

  useEffect(() => {
    appDispatch(setCustomerValue(hotelPricing.price))
  }, [appDispatch, hotelPricing.price])

  const showPrices = packageView.hotelAvailable && packageView.capacityValid

  const showHotelPriceWithFlights = !offer.bundledWithFlightsOnly && !!flightsEnabled && !hideNoCacheMessaging && hasFlights && availableForPurchase

  const shouldTrackPrice = !pricesLoading && !!pricingToDisplay?.price && (showHotelPriceWithFlights ? showHotelPrice : availableForPurchase && showPrices && showHotelPrice)
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: pricingToDisplay.price.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, pricingToDisplay?.price, offer?.id])

  if (showHotelPriceWithFlights) {
    return <SummaryPane border="neutral-six" borderRadius="XS" padding="28 20" shadow="bottom-small" className={className} data-testid="SummaryPane">
      <WalledContent
        enforced={offer.walledGarden}
        wall={<BodyText variant="large" weight="bold">
          <FormattedMessage
            id="VqLBcTtR/P"
            defaultMessage="{durationLabel} packages available"
            description="Walled content message showing available packages for hotel+flights offer"
            values={{ durationLabel: offer.durationLabel }}
          />
        </BodyText>}
      >
        <Group direction="vertical" gap={12} horizontalAlign="center">
          <LoadingIndicator floating backdrop="white" opaque visible={pricesLoading} />

          <Group direction="vertical" fullWidth gap={16} horizontalAlign="center">
            {showDiscountPill && <PriceRowValueDiscount type="estimate" discountPercentage={valuedUpTo.percent} />}
            <AccommodationPackageTilePricingBlocks>
              <VerticalSpacer gap={12}>
                <BodyTextBlock variant="large" weight="bold">
                  <FormattedMessage
                    id="svWSMeBTt2"
                    defaultMessage="Hotel"
                    description="Pricing label for hotel package on the hotel or room booking page"
                  />
                </BodyTextBlock>
                <VerticalSpacer gap={8}>
                  <div>
                    <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                    {showHotelPrice && pricingToDisplay && <PriceRowPrice
                      data-testid="pricing"
                      size="L"
                      price={pricingToDisplay.price}
                      saleUnit={saleUnit}
                    />}
                    {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />}
                  </div>
                  <TextButton
                    kind="secondary"
                    data-testid="OfferAvailable"
                    onClick={onViewPackageOptions}
                    fit="flex"
                  >
                    {viewOptionsShortLabel}
                  </TextButton>
                </VerticalSpacer>
              </VerticalSpacer>
              <VerticalSpacer gap={12}>
                <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">
                  <FormattedMessage
                    id="xazzvwoixR"
                    defaultMessage="Hotel + flights"
                    description="Pricing label for hotel and flights package in the flights flow"
                  />
                </BodyTextBlock>
                <VerticalSpacer gap={8}>
                  <div>
                    <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                    <PriceRowPrice
                      data-testid="FlightPrice"
                      size="L"
                      price={(showMemberPrice ? memberPricingToDisplay.price : pricingToDisplay!.price) + flightPrice}
                      saleUnit={saleUnit}
                    />
                    {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />}
                  </div>
                  <TextButton
                    kind="secondary"
                    data-testid="OfferAvailable"
                    onClick={onViewPackageOptions}
                    fit="flex"
                  >
                    {viewOptionsShortLabel}
                  </TextButton>
                </VerticalSpacer>
              </VerticalSpacer>
            </AccommodationPackageTilePricingBlocks>
            <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
          </Group>
          {showHotelPrice && <PriceRowTaxesAndFees
            endIcon={showPriceDetails && <OfferPriceDetailsRow
              offerPage
              offer={offer}
              duration={duration ?? pkg.duration}
              propertyFees={fees.property}
              taxesAndFees={fees.taxesAndFees}
              flightPrice={offer.bundledWithFlightsOnly ? flightPrice : undefined}
              hotelPrice={priceWithoutPropertyFees(hotelPricing)}
              rooms={rooms}
              extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
              dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
              trigger="info-icon"
              cta={availableForPurchase && <TextButton
                kind="primary"
                fit="flex"
              >
                {viewOptionsLabel}
              </TextButton>}
              onCtaAfterClose={onViewPackageOptions}
            />}
          />}
        </Group>
      </WalledContent>
    </SummaryPane>
  }

  const hotelFlightsPackagesLabel = intl.formatMessage({
    id: 'br0R0zUhAA',
    defaultMessage: 'Hotel + flights packages available',
    description: 'Label for bundled hotel and flights packages available on offer page',
  })
  const hotelFlightsLabel = intl.formatMessage({
    id: 'ifwMQKLQGe',
    defaultMessage: 'Hotel + flights',
    description: 'Form field label for bundled hotel and flights on the offer page',
  })

  return (
    <SummaryPane border="neutral-five" shadow="bottom-small" className={className} data-testid="SummaryPane">
      {availableForPurchase && (
        <WalledContent
          enforced={offer.walledGarden}
          wall={<BodyText variant="large" weight="bold">
            <FormattedMessage
              id="p2DcS1pcVi"
              defaultMessage="{durationLabel} packages available"
              description="Walled content message showing available packages for hotel offer"
              values={{ durationLabel: offer.durationLabel }}
            />
          </BodyText>}
        >
          {showPrices && <Group direction="vertical" horizontalAlign="center" gap={4}>
            <PriceDetails>
              <LoadingIndicator floating backdrop="white" opaque visible={pricesLoading} />
              {showSecureWithDepositTag && <SecureWithDepositTagAndTooltip offerType={offer.type} /> }
              {offer.bundledWithFlightsOnly &&
                <CaptionTextBlock variant="large" weight="bold" startIcon={<LinePlaneFlyIcon />}>
                  {hideNoCacheMessaging ? hotelFlightsPackagesLabel : hotelFlightsLabel}
                </CaptionTextBlock>}
              {showHotelPrice && pricingToDisplay && <>
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <PriceRowPrice
                  data-testid="pricing"
                  size="L"
                  price={pricingToDisplay.price}
                  saleUnit={saleUnit}
                />
              </>}
              {showHotelPrice && <PriceRowTaxesAndFees
                endIcon={showPriceDetails && <OfferPriceDetailsRow
                  offerPage
                  offer={offer}
                  duration={duration ?? pkg.duration}
                  propertyFees={fees.property}
                  taxesAndFees={fees.taxesAndFees}
                  flightPrice={offer.bundledWithFlightsOnly ? flightPrice : undefined}
                  hotelPrice={priceWithoutPropertyFees(hotelPricing)}
                  rooms={rooms}
                  extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                  dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                  trigger="info-icon"
                  cta={availableForPurchase && <TextButton
                    kind="primary"
                    fit="flex"
                  >
                    {viewOptionsLabel}
                  </TextButton>}
                  onCtaAfterClose={onViewPackageOptions}
                />}
              />}
              {showIncludedGuestsExceeded &&
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={valuedUpTo.value}
                discountPercentage={showDiscountPill ? valuedUpTo.percent : undefined}
              />}
              <LoyaltyPointsWrapper>
                <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" horizontalAlign="center" />
              </LoyaltyPointsWrapper>
              {flightsEnabled &&
                <FlightPrice>
                  {!hasFlights && flightOrigin.code === flightDestination?.code && (
                    <ChangeDepartureWrapper>
                      <Caption variant="large" colour="neutral-three">
                        {intl.formatMessage(
                          {
                            id: '1jNYFEDgP6',
                            defaultMessage: 'No flights available from {origin}. Please select another airport.',
                            description: 'Error message when no flights are available from selected airport on hotel offer page',
                          },
                          { origin: flightOrigin.name },
                        )}
                      </Caption>
                      <TextLink
                        onClick={onToggleFilter}
                        data-filter="airport"
                      >
                        {intl.formatMessage({
                          id: 'Grrp2JY8yJ',
                          defaultMessage: 'Change departure airport',
                          description: 'Link text to change departure airport on hotel offer page',
                        })}
                      </TextLink>
                    </ChangeDepartureWrapper>
                  )}
                  {!hasFlights && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && (
                    <Caption variant="large" colour="neutral-three" align="center">
                      {intl.formatMessage({
                        id: 'RRAkIiS/By',
                        defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
                        description: 'Info message about adding flights to hotel package on offer page',
                      })}
                    </Caption>
                  )}
                  {!hasFlights && departureTooFarInFuture && (
                    <ChangeDepartureWrapper>
                      <Caption variant="large" colour="neutral-three">
                        {intl.formatMessage({
                          id: 'TAv9aA1FU8',
                          defaultMessage: 'Flights can only be booked up to 11 months in advance.',
                          description: 'Error message when departure date is too far in the future on hotel offer page',
                        })}
                      </Caption>
                    </ChangeDepartureWrapper>
                  )}
                  {hasFlights && !offer?.bundledWithFlightsOnly && <PriceRowFlightPackage
                    data-testid="FlightPrice"
                    product="hotel"
                    price={(showMemberPrice ? memberPricingToDisplay.price : pricingToDisplay!.price) + flightPrice}
                  />}
                  <AtolProtectedTooltip />
                </FlightPrice>}
            </PriceDetails>
            <PriceRowAgentHubCommission
              offerId={offer.id}
              offerLocations={offerLocations}
              vendorName={vendorName}
              productType={offer.productType}
              packageId={pkg.id}
            />

          </Group>}
        </WalledContent>
      )}
      {availableForPurchase && <TextButton
        kind="primary"
        data-testid="OfferAvailable"
        onClick={onViewPackageOptions}
        fit="flex"
      >
        {viewOptionsLabel}
      </TextButton>}
      {!availableForPurchase && <>
        <BodyText
          variant="large"
          data-testid="OfferSoldOut"
          weight="bold"
        >
          {intl.formatMessage({
            id: 'z/O0g0fDul',
            defaultMessage: 'This offer has SOLD OUT',
            description: 'Status text when hotel offer is sold out on the offer page',
          })}
        </BodyText>
        <TextButton
          kind="primary"
          to={`/${OFFER_TYPE_HOTEL_SLUG}`}
          fit="flex"
        >
          {intl.formatMessage({
            id: 'hKvWBv0Mpa',
            defaultMessage: 'Browse all hotel offers',
            description: 'Link text to browse all hotel offers when current offer is sold out',
          })}
        </TextButton>
      </>}
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  pricesLoading: arePricesLoading(appState, state),
  baseRatePackagesView: getBaseRatePackagesView(appState, state),
  checkIn: state.checkInDate,
  duration: state.duration,
  offer: state.offer,
  flightOrigin: state.airport,
  rooms: state.rooms,
  durationLabel: getFromDurationLabel(state, appState),
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnSummary, preventRenderWithOpenFilter)
